/* Keep the profile and widgets pinned together by their padded bottom. */
(function () {
    const sidebar = document.querySelector('.fw-sidebar-content');
    if (!sidebar) return;

    function measure() {
        // offsetHeight includes bottom padding and ignores entrance animation transforms.
        sidebar.style.setProperty('--fw-sidebar-height', sidebar.offsetHeight + 'px');
    }

    // Chat loading, expand/collapse controls and font changes can all change the height.
    const observer = new ResizeObserver(measure);
    observer.observe(sidebar);
    measure();
})();

/* Apply Fuwari interactions independently of Ghost's Portal and editor-card scripts. */
(function () {
    const labels = document.getElementById('fw-i18n').dataset;
    const toggles = Array.from(document.querySelectorAll('[data-fw-panel]'));

    // Synchronize panel visibility and expanded state without hiding the desktop navigation.
    function setPanel(button, open, restoreFocus) {
        const panel = document.getElementById(button.dataset.fwPanel);
        button.setAttribute('aria-expanded', String(open));
        if (panel.id === 'fw-menu') panel.classList.toggle('fw-open', open);
        else panel.hidden = !open;
        if (!open && restoreFocus) button.focus();
    }

    toggles.forEach(function (button) {
        button.addEventListener('click', function () {
            const open = button.getAttribute('aria-expanded') !== 'true';
            toggles.forEach(function (other) { setPanel(other, other === button && open, false); });
            // Move keyboard focus into the opened panel before subsequent toolbar buttons.
            if (open) document.getElementById(button.dataset.fwPanel).querySelector('a,button,select,input')?.focus();
        });
    });
    document.addEventListener('click', function (event) {
        toggles.forEach(function (button) {
            const panel = document.getElementById(button.dataset.fwPanel);
            if (!button.contains(event.target) && !panel.contains(event.target)) setPanel(button, false, false);
        });
    });
    document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        toggles.forEach(function (button) {
            if (button.getAttribute('aria-expanded') === 'true') setPanel(button, false, true);
        });
    });
    document.addEventListener('focusin', function (event) {
        toggles.forEach(function (button) {
            if (button.getAttribute('aria-expanded') !== 'true') return;
            const panel = document.getElementById(button.dataset.fwPanel);
            if (!button.contains(event.target) && !panel.contains(event.target)) setPanel(button, false, false);
        });
    });
    matchMedia('(min-width: 768px)').addEventListener('change', function () {
        const menuToggle = document.querySelector('[aria-controls="fw-menu"]');
        setPanel(menuToggle, false, false);
    });

    // Collapse long category and tag lists after fonts settle.
    document.querySelectorAll('.fw-expand').forEach(function (expand) {
        const list = document.getElementById(expand.getAttribute('aria-controls'));
        if (!list) return;
        // Keep short lists open and expose expansion only when content is actually clipped.
        function measure() {
            if (expand.getAttribute('aria-expanded') === 'true') return;
            list.classList.add('fw-collapsed');
            expand.hidden = list.classList.contains('fw-categories') ? list.children.length <= 5 : list.scrollHeight <= list.clientHeight + 1;
        }
        measure();
        document.fonts.ready.then(measure);
        window.addEventListener('resize', measure);
        expand.addEventListener('click', function () {
            const edge = list.getBoundingClientRect().bottom;
            const next = list.classList.contains('fw-categories') ? list.querySelector('li:nth-child(6) a') : Array.from(list.querySelectorAll('a')).find(function (tag) { return tag.getBoundingClientRect().top >= edge; });
            expand.setAttribute('aria-expanded', 'true');
            list.classList.remove('fw-collapsed');
            expand.hidden = true;
            if (next) next.focus({preventScroll: true});
        });
    });

    // Reproduce Fuwari's five-page window with endpoints and single-page gaps filled.
    document.querySelectorAll('[data-fw-pagination]').forEach(function (nav) {
        const current = Number(nav.dataset.page);
        const total = Number(nav.dataset.pages);
        const first = new URL(nav.dataset.first, location.href);
        if (!Number.isInteger(current) || !Number.isInteger(total) || total < 1) return;
        const left = Math.max(1, Math.min(current - 2, total - 4));
        const right = Math.min(total, left + 4);
        const pages = new Set([1, total]);
        for (let number = left; number <= right; number++) pages.add(number);
        if (left === 3) pages.add(2);
        if (right === total - 2) pages.add(total - 1);
        const holder = nav.querySelector('.fw-page-numbers');
        holder.textContent = '';
        let previous = 0;
        Array.from(pages).sort(function (a, b) { return a - b; }).forEach(function (number) {
            if (number - previous > 1) {
                const gap = document.createElement('span');
                gap.textContent = '…';
                holder.appendChild(gap);
            }
            const element = document.createElement(number === current ? 'span' : 'a');
            element.textContent = number;
            if (number === current) element.setAttribute('aria-current', 'page');
            else {
                const target = new URL(first);
                if (number !== 1) target.pathname = first.pathname.replace(/\/?$/, '/') + 'page/' + number + '/';
                element.href = target.href;
                element.setAttribute('aria-label', labels.page.replace('{number}', number));
            }
            holder.appendChild(element);
            previous = number;
        });
    });

    const archive = document.querySelector('[data-fw-archive]');
    if (!archive) return;
    const status = archive.querySelector('[data-fw-archive-status]');
    const retry = archive.querySelector('[data-fw-archive-retry]');
    const clear = archive.querySelector('[data-fw-archive-clear]');
    const archiveList = archive.querySelector('[data-fw-archive-list]');
    const tagFilter = archive.querySelector('[data-fw-archive-tag]');
    const tagNames = new Map();
    let posts = [];
    let complete = false;

    // Filter only metadata already published in public cards, never protected body HTML.
    function renderArchive() {
        const query = new URLSearchParams(location.search);
        const tag = query.get('tag');
        tagFilter.value = tag || '';
        const filtered = posts.filter(function (post) {
            return !tag || post.tags.includes(tag);
        });
        archiveList.textContent = '';
        const groups = new Map();
        filtered.forEach(function (post) {
            const year = post.date.slice(0, 4);
            if (!groups.has(year)) groups.set(year, []);
            groups.get(year).push(post);
        });
        groups.forEach(function (entries, year) {
            const group = document.createElement('section');
            group.className = 'fw-archive-year';
            const header = document.createElement('header');
            const heading = document.createElement('h2');
            heading.textContent = year;
            const marker = document.createElement('span');
            marker.className = 'fw-archive-year-marker';
            marker.setAttribute('aria-hidden', 'true');
            const count = document.createElement('span');
            count.className = 'fw-archive-count';
            count.textContent = labels.posts.replace('{number}', entries.length);
            header.append(heading, marker, count);
            group.appendChild(header);
            const list = document.createElement('ul');
            entries.forEach(function (post) {
                const row = document.createElement('li');
                const link = document.createElement('a');
                link.className = 'fw-archive-entry';
                link.href = post.url;
                link.setAttribute('aria-label', post.title);
                const date = document.createElement('time');
                date.dateTime = post.date;
                date.textContent = post.date.slice(5);
                const track = document.createElement('span');
                track.className = 'fw-archive-track';
                track.setAttribute('aria-hidden', 'true');
                const title = document.createElement('span');
                title.className = 'fw-archive-entry-title';
                title.textContent = post.title;
                const tags = document.createElement('span');
                tags.className = 'fw-archive-entry-tags';
                tags.textContent = post.tags.map(function (slug) { return '#' + (tagNames.get(slug) || slug); }).join(' ');
                link.append(date, track, title, tags);
                row.appendChild(link);
                list.appendChild(row);
            });
            group.appendChild(list);
            archiveList.appendChild(group);
        });
        clear.hidden = !tag;
        status.classList.toggle('fw-sr-only', complete && filtered.length > 0);
        status.textContent = complete ? (filtered.length ? labels.posts.replace('{number}', filtered.length) : labels.empty) : labels.loading.replace('{number}', posts.length);
    }

    // Follow Ghost's real collection pagination and reject loops, redirects and incomplete feeds.
    async function loadArchive() {
        posts = [];
        complete = false;
        retry.hidden = true;
        tagFilter.disabled = true;
        tagNames.clear();
        archive.setAttribute('aria-busy', 'true');
        const visited = new Set();
        const ids = new Set();
        let next = new URL(archive.dataset.start, location.href);
        try {
            while (next) {
                if (next.origin !== location.origin || visited.has(next.href)) throw new Error('Invalid archive pagination.');
                visited.add(next.href);
                const response = await fetch(next.href, {credentials: 'omit', signal: AbortSignal.timeout(15000)});
                if (!response.ok || response.redirected) throw new Error('Archive request failed.');
                const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
                const feed = doc.querySelector('.fw-main > .fw-feed');
                if (!feed) throw new Error('The home route is not a complete post collection.');
                const cards = feed.querySelectorAll('[data-fw-post]');
                cards.forEach(function (card) {
                    const title = card.querySelector('[data-fw-title]');
                    if (!title || !/^\d{4}-\d{2}-\d{2}$/.test(card.dataset.fwDate)) throw new Error('Invalid archive card.');
                    if (ids.has(card.dataset.fwId)) throw new Error('Duplicate archive post; the collection changed during loading.');
                    ids.add(card.dataset.fwId);
                    const url = new URL(title.getAttribute('href'), next);
                    if (url.origin !== location.origin) throw new Error('Invalid archive URL.');
                    card.querySelectorAll('[data-fw-tag]').forEach(function (item) { tagNames.set(item.dataset.fwTag, item.textContent); });
                    posts.push({title: title.textContent, url: url.href, date: card.dataset.fwDate, tags: Array.from(card.querySelectorAll('[data-fw-tag]')).map(function (item) { return item.dataset.fwTag; })});
                });
                posts.sort(function (a, b) { return b.date.localeCompare(a.date); });
                renderArchive();
                const pagination = doc.querySelector('[data-fw-pagination]');
                const following = pagination && pagination.querySelector('a[rel="next"]');
                if (pagination && Number(pagination.dataset.page) < Number(pagination.dataset.pages) && !following) throw new Error('Missing next archive page.');
                next = following ? new URL(following.getAttribute('href'), next) : null;
            }
            complete = true;
            // Enable filters only after the entire public collection has been gathered.
            tagFilter.replaceChildren(new Option(labels.allTags, ''));
            Array.from(tagNames).sort(function (a, b) { return a[1].localeCompare(b[1]); }).forEach(function (entry) { tagFilter.add(new Option(entry[1], entry[0])); });
            tagFilter.disabled = false;
            renderArchive();
        } catch (error) {
            status.classList.remove('fw-sr-only');
            status.textContent = labels.incomplete.replace('{number}', posts.length);
            retry.hidden = false;
        } finally {
            archive.setAttribute('aria-busy', 'false');
        }
    }
    // Preserve shareable query filters and browser history without another content download.
    function changeFilter() {
        const url = new URL(location.href);
        url.searchParams.delete('tag');
        if (tagFilter.value) url.searchParams.set('tag', tagFilter.value);
        history.pushState(null, '', url.pathname + url.search);
        renderArchive();
    }
    tagFilter.addEventListener('change', changeFilter);
    retry.addEventListener('click', loadArchive);
    clear.addEventListener('click', function () { history.replaceState(null, '', location.pathname); renderArchive(); });
    window.addEventListener('popstate', renderArchive);
    loadArchive();
})();

/* Match Fuwari's numbered TOC, viewport section range and banner-dependent visibility. */
(function () {
    const toc = document.querySelector('.fw-toc');
    const content = document.querySelector('.fw-content');
    const top = document.querySelector('.fw-top');
    const headings = content && document.body.classList.contains('post-template') ? Array.from(content.querySelectorAll('h1,h2,h3,h4,h5,h6')).filter(function (heading) {
        return !heading.closest('.kg-card,.gh-post-upgrade-cta,[hidden]');
    }) : [];
    const shallowest = Math.min.apply(null, headings.map(function (heading) { return Number(heading.tagName.slice(1)); }));
    const visible = headings.filter(function (heading) { return Number(heading.tagName.slice(1)) < shallowest + 2; });
    // Heading relationships are fixed: resolve each section's end once, not on every scroll.
    const sectionEnds = visible.map(function (heading, index) {
        return visible.findIndex(function (next, nextIndex) { return nextIndex > index && next.tagName <= heading.tagName; });
    });
    const links = [];
    let number = 0;
    let indicator;
    if (toc && visible.length) {
        const list = document.createElement('ol');
        visible.forEach(function (heading) {
            const depth = Number(heading.tagName.slice(1)) - shallowest;
            const item = document.createElement('li');
            const link = document.createElement('a');
            link.href = '#' + encodeURIComponent(heading.id);
            link.style.setProperty('--fw-depth', depth);
            const badge = document.createElement('span');
            badge.className = 'fw-toc-badge';
            badge.setAttribute('aria-hidden', 'true');
            badge.textContent = depth ? '' : ++number;
            if (depth) badge.classList.add('fw-toc-dot');
            const label = document.createElement('span');
            const title = heading.cloneNode(true);
            title.querySelectorAll('.fw-heading-anchor').forEach(function (anchor) { anchor.remove(); });
            label.textContent = title.textContent;
            link.append(badge, label);
            item.appendChild(link);
            list.appendChild(item);
            links.push(link);
        });
        indicator = document.createElement('div');
        indicator.className = 'fw-toc-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        toc.append(list, indicator);
    }
    // One animation-frame update handles both controls without repeated scroll-layout work.
    function update() {
        const threshold = innerHeight * .35;
        const shown = scrollY > threshold;
        if (top) {
            top.hidden = false;
            top.classList.toggle('fw-control-hidden', !shown);
            top.inert = !shown;
            top.setAttribute('aria-hidden', String(!shown));
        }
        if (!toc || !links.length) return;
        toc.hidden = false;
        const tocShown = !document.body.classList.contains('fw-has-banner') || shown;
        toc.classList.toggle('fw-control-hidden', !tocShown);
        toc.inert = !tocShown;
        toc.setAttribute('aria-hidden', String(!tocShown));
        let current = 0;
        const positions = visible.map(function (heading) { return heading.getBoundingClientRect().top; });
        const contentBottom = content.getBoundingClientRect().bottom;
        positions.forEach(function (position, index) { if (position <= 80) current = index; });
        links.forEach(function (link, index) {
            if (index === current) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
            // A parent section ends at the next equal or shallower heading, as in the reference.
            const next = sectionEnds[index];
            const bottom = next < 0 ? contentBottom : positions[next];
            link.classList.toggle('fw-toc-visible', positions[index] < innerHeight && bottom > 80);
        });
        const range = links.filter(function (link) { return link.classList.contains('fw-toc-visible'); });
        indicator.hidden = !range.length;
        if (range.length) {
            indicator.style.top = range[0].offsetTop + 'px';
            indicator.style.height = (range[range.length - 1].offsetTop + range[range.length - 1].offsetHeight - range[0].offsetTop) + 'px';
        }
        const active = links[current];
        if (tocShown && !toc.matches(':hover,:focus-within')) {
            const bounds = active.getBoundingClientRect();
            const viewport = toc.getBoundingClientRect();
            if (bounds.top < viewport.top || bounds.bottom > viewport.bottom) toc.scrollTop += bounds.top - viewport.top - 32;
        }
    }
    let pending = false;
    // Coalesce scroll, resize and late content layout changes into the next paint.
    function schedule() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; update(); });
    }
    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule);
    document.fonts.ready.then(schedule);
    window.addEventListener('load', schedule);
    if (content) new ResizeObserver(schedule).observe(content);
    if (top) top.addEventListener('click', function () {
        window.scrollTo({top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    });
    update();
})();

/* Style only accessible Ghost Portal frames while leaving authentication and payment logic to Ghost. */
(function () {
    const source = document.currentScript;
    // Ghost versions the script URL on theme updates; keep that version on iframe CSS too.
    function stylesheet(path) {
        const url = new URL(path, source.src);
        url.search = new URL(source.src).search;
        // The minifier resolves this build constant from the standalone iframe CSS hash.
        url.searchParams.set('fw-theme', __FW_FRAME_STYLE_VERSION__);
        return url.href;
    }
    const stylesheets = {
        'portal-popup': stylesheet('../css/portal.css'),
        'portal-trigger': stylesheet('../css/portal.css'),
        'portal-notification': stylesheet('../css/portal.css'),
        'comments-frame': stylesheet('../css/comments.css'),
        addDetailsPopup: stylesheet('../css/comments-profile.css'),
        search: stylesheet('../css/search.css')
    };
    const root = document.documentElement;
    const frames = new WeakSet();
    const tokens = ['--fw-primary', '--fw-card', '--fw-ink', '--fw-muted', '--fw-soft', '--fw-line', '--fw-hue', '--fw-button-hover', '--fw-button-active'];
    function palette() {
        const style = getComputedStyle(root);
        return {values: tokens.map(function (name) { return style.getPropertyValue(name); }), dark: root.classList.contains('fw-dark')};
    }
    // Copy resolved theme tokens across the iframe boundary without inspecting member data.
    function apply(frame, theme) {
        try {
            const doc = frame.contentDocument;
            if (!doc || !doc.head) return;
            let link = doc.getElementById('fw-portal-theme');
            if (!link) {
                link = doc.createElement('link');
                link.id = 'fw-portal-theme';
                link.rel = 'stylesheet';
                doc.head.appendChild(link);
            }
            const href = stylesheets[frame.title] || stylesheets.search;
            if (link.href !== href) link.href = href;
            tokens.forEach(function (name, index) {
                doc.documentElement.style.setProperty(name, theme.values[index]);
            });
            doc.documentElement.style.colorScheme = theme.dark ? 'dark' : 'light';
            if (frame.title === 'comments-frame' || frame.title === 'addDetailsPopup') doc.documentElement.classList.toggle('dark', theme.dark);
        } catch (error) { /* Cross-origin frames retain Ghost's native appearance. */ }
    }
    // Portal mounts on demand; observe frame additions rather than polling or patching its React tree.
    function sync() {
        let theme;
        document.querySelectorAll('iframe[title="portal-popup"], iframe[title="portal-trigger"], iframe[title="portal-notification"], iframe[title="comments-frame"], iframe[title="addDetailsPopup"], #sodo-search-root iframe').forEach(function (frame) {
            if (!frames.has(frame)) {
                frames.add(frame);
                frame.addEventListener('load', function () { apply(frame, palette()); });
            }
            if (!theme) theme = palette();
            apply(frame, theme);
        });
    }
    new MutationObserver(sync).observe(document.body, {childList: true, subtree: true});
    new MutationObserver(sync).observe(root, {attributes: true, attributeFilter: ['class', 'style']});
    sync();
})();

/* Restore Fuwari's separate color panel and hover menu without hiding mobile theme controls. */
(function () {
    const root = document.documentElement;
    const hue = document.getElementById('fw-hue');
    const cycle = document.querySelector('[data-fw-cycle]');
    const labels = document.getElementById('fw-i18n').dataset;
    const control = document.querySelector('.fw-mode-control');
    const panel = document.getElementById('fw-mode-panel');
    if (!hue || !cycle || !control || !panel) return;
    const choices = Array.from(panel.querySelectorAll('[data-fw-mode]'));
    const system = matchMedia('(prefers-color-scheme: dark)');
    let mode = root.dataset.fwMode || 'auto';
    // Persist preferences without making storage access a prerequisite for controls.
    function save(key, value) { try { localStorage.setItem(key, value); } catch (error) { /* Keep the current page preference. */ } }
    // Synchronize selected buttons, the toolbar icon and automatic system appearance.
    function applyMode() {
        root.dataset.fwMode = mode;
        root.classList.toggle('fw-dark', mode === 'dark' || (mode === 'auto' && system.matches));
        choices.forEach(function (button) {
            button.setAttribute('aria-pressed', String(button.dataset.fwMode === mode));
            if (button.dataset.fwMode === mode) cycle.replaceChildren(button.querySelector('svg').cloneNode(true));
        });
        cycle.setAttribute('aria-label', labels.theme.replace('{mode}', labels[mode]));
    }
    // Keep color input, value badge and conditional reset icon in agreement.
    function applyHue(value) {
        const numeric = Number(value);
        const color = Number.isFinite(numeric) ? Math.max(0, Math.min(360, numeric)) : 250;
        root.style.setProperty('--fw-hue', color);
        hue.value = color;
        document.getElementById('fw-hue-value').value = color;
        const reset = document.querySelector('[data-fw-reset]');
        reset.classList.toggle('fw-reset-default', color === 250);
        reset.disabled = color === 250;
    }
    // The desktop hover region includes its padding bridge so moving into the menu keeps it open.
    function show(open) { panel.hidden = !open; cycle.setAttribute('aria-expanded', String(open)); }
    control.addEventListener('mouseenter', function () { if (matchMedia('(hover: hover) and (min-width: 1024px)').matches) show(true); });
    control.addEventListener('mouseleave', function () { if (!control.querySelector(':focus-visible')) show(false); });
    cycle.addEventListener('click', function () {
        const sequence = ['light', 'dark', 'auto'];
        mode = sequence[(sequence.indexOf(mode) + 1) % sequence.length];
        applyMode(); save('fw-mode', mode);
    });
    cycle.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') { event.preventDefault(); show(true); choices[0].focus(); }
    });
    choices.forEach(function (button) {
        button.addEventListener('click', function () { mode = button.dataset.fwMode; applyMode(); save('fw-mode', mode); });
    });
    document.addEventListener('click', function (event) { if (!control.contains(event.target)) show(false); });
    control.addEventListener('focusout', function (event) { if (!control.contains(event.relatedTarget)) show(false); });
    control.addEventListener('keydown', function (event) { if (event.key === 'Escape') { show(false); cycle.focus(); } });
    hue.addEventListener('input', function () { applyHue(hue.value); save('fw-hue', hue.value); });
    document.querySelector('[data-fw-reset]').addEventListener('click', function () { applyHue(250); save('fw-hue', '250'); });
    system.addEventListener('change', applyMode);
    applyHue(root.style.getPropertyValue('--fw-hue') || 250);
    applyMode();
})();

/* Follow the banner while scrolling, then hide the header at Fuwari's content threshold. */
(function () {
    const sticky = document.querySelector('.fw-nav-sticky');
    if (!sticky) return;
    // Use the live reference's home/internal banner heights and 72+56+16px overlap offset.
    function update() {
        const banner = document.body.classList.contains('fw-has-banner');
        const home = document.body.classList.contains('home-template') && innerWidth >= 1024;
        const threshold = innerHeight * (home ? .65 : .35) - 144;
        const hidden = banner && scrollY >= Math.max(0, threshold);
        sticky.classList.toggle('fw-nav-hidden', hidden);
        sticky.inert = hidden;
        sticky.setAttribute('aria-hidden', String(hidden));
    }
    let pending = false;
    // Batch scroll events so sticky presentation is updated once per frame.
    function schedule() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () { pending = false; update(); });
    }
    addEventListener('scroll', schedule, {passive: true});
    addEventListener('resize', schedule);
    update();
})();

/* Keep document scroll progress visible even when the banner navigation hides. */
(function () {
    if (!window.ProgressBar) return;
    const root = document.documentElement;
    const indicator = document.createElement('div');
    indicator.className = 'fw-scroll-progress';
    indicator.setAttribute('aria-hidden', 'true');
    document.body.appendChild(indicator);
    const bar = new ProgressBar.Line(indicator, {strokeWidth: 3, color: 'currentColor', svgStyle: null});
    const svg = bar.svg;
    const namespace = 'http://www.w3.org/2000/svg';
    const definitions = document.createElementNS(namespace, 'defs');
    const gradient = document.createElementNS(namespace, 'linearGradient');
    gradient.id = 'fw-scroll-gradient';
    // SVG lines have no bounding-box height, so resolve the gradient in path coordinates.
    gradient.setAttribute('gradientUnits', 'userSpaceOnUse');
    gradient.setAttribute('x1', '0');
    gradient.setAttribute('x2', '100');
    gradient.setAttribute('y1', '0');
    gradient.setAttribute('y2', '0');
    ['0%', '50%', '100%'].forEach(function (offset) {
        const stop = document.createElementNS(namespace, 'stop');
        stop.setAttribute('offset', offset);
        gradient.appendChild(stop);
    });
    definitions.appendChild(gradient);
    svg.prepend(definitions);
    bar.path.setAttribute('stroke', 'url(#fw-scroll-gradient)');

    let pending = false;
    let previous = -1;

    // Read live scroll bounds before writes; entrance transforms can change overflow without a resize.
    function update() {
        pending = false;
        const distance = root.scrollHeight - root.clientHeight;
        const progress = distance > 1 ? Math.max(0, Math.min(1, window.scrollY / distance)) : 0;
        const hidden = distance <= 1;
        if (indicator.hidden !== hidden) indicator.hidden = hidden;
        // Avoid the library's computed-style read and SVG writes when progress has not changed.
        if (progress === previous) return;
        previous = progress;
        bar.set(progress);
    }

    // Combine scroll events into one update per paint without tweening behind the reader.
    function schedule() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    window.addEventListener('pageshow', schedule);
    document.fonts.ready.then(schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    observer.observe(root);
    update();
})();
